import { createFileRoute, Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type ClipboardEvent } from 'react'
import { api, type WorkDetail, type WorkKind, type WorkPatch, type WorkSide, type WorkStatus } from '../lib/api'
import { useCurrentUser, isAdminRole } from '../lib/auth'
import {
  CUSTOMER_NAME,
  TEAM_ONLY_STATUSES,
  WORK_BADGE_KEY,
  WORK_KINDS,
  WORK_LIST_KEY,
  WORK_PRIORITIES,
  WORK_STATUSES,
  fmtWhen,
  imagesFromClipboard,
  kindMeta,
  shrinkImage,
  sideGenitive,
  sideLabel,
  statusMeta,
  workItemKey,
} from '../lib/work'
import { PlaceInput, StatusBadge } from './verkefni'
import { ReviewPanel } from '../components/work/ReviewPanel'

export const Route = createFileRoute('/verkefni_/$number')({
  component: WorkItemPage,
})

const card = 'rounded-[14px] border border-[var(--border)] bg-[var(--surface)]'
const h2 = 'text-xs font-semibold uppercase tracking-wide text-[var(--muted)]'
const selectCls =
  'w-full rounded-[11px] border border-[var(--border-strong)] bg-[var(--surface)] px-2.5 py-2 text-sm text-[var(--fg)] outline-none focus:border-[var(--brand)]'

function WorkItemPage() {
  const { number: raw } = Route.useParams()
  const number = Number(raw)
  const { data: me, isLoading: meLoading } = useCurrentUser()
  const admin = isAdminRole(me)
  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery({ queryKey: workItemKey(number), queryFn: () => api.work.get(number), enabled: admin && Number.isInteger(number) })
  const side: WorkSide = me && (me.role === 'superadmin' || me.email.toLowerCase().endsWith('@laufid.is')) ? 'team' : 'customer'

  useEffect(() => {
    if (!data) return
    qc.invalidateQueries({ queryKey: WORK_BADGE_KEY })
    qc.invalidateQueries({ queryKey: WORK_LIST_KEY })
  }, [data, qc])

  const settle = (fresh: WorkDetail) => {
    qc.setQueryData(workItemKey(number), fresh)
    qc.invalidateQueries({ queryKey: WORK_LIST_KEY })
    qc.invalidateQueries({ queryKey: WORK_BADGE_KEY })
  }
  const patch = useMutation({ mutationFn: (body: WorkPatch) => api.work.patch(number, body), onSuccess: settle })
  const send = useMutation({ mutationFn: (body: string) => api.work.message(number, body), onSuccess: settle })
  const attach = useMutation({ mutationFn: (blob: Blob) => api.work.attach(number, blob), onSuccess: settle })
  const detach = useMutation({
    mutationFn: (id: string) => api.work.removeAttachment(number, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: workItemKey(number) })
      qc.invalidateQueries({ queryKey: WORK_LIST_KEY })
    },
  })

  const [draft, setDraft] = useState('')
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState<string | null>(null)
  const [shotError, setShotError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [where, setWhere] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  if (meLoading) return <p className="p-8 text-[var(--muted)]">Hleð…</p>
  if (!admin) return <p className="p-8 text-[var(--muted)]">Þú þarft stjórnandaaðgang til að skoða beiðnir.</p>
  if (!Number.isInteger(number) || number < 1)
    return (
      <div className="mx-auto mt-10 max-w-xl rounded-[14px] border border-dashed border-[var(--border-strong)] bg-[var(--surface)] p-10 text-center">
        <p className="text-sm text-[var(--fg-soft)]">Engin beiðni ber númerið „{raw}“.</p>
        <Link to="/verkefni" search={{}} className="mt-2 inline-block text-sm font-medium text-[var(--brand)] hover:underline">
          Til baka í verkefnalistann
        </Link>
      </div>
    )
  if (error instanceof Error)
    return <div className="mx-auto mt-10 max-w-xl rounded-[14px] border border-[#dc2626]/30 bg-[rgba(220,38,38,.08)] p-4 text-sm text-[#dc2626]">{error.message}</div>
  if (isLoading || !data) return <p className="p-8 text-[var(--muted)]">Hleð…</p>

  const { item, messages, attachments } = data
  const waitingOnMe = item.awaitingSide === side && statusMeta(item.status).open

  const addFiles = async (files: Iterable<File | Blob>) => {
    setShotError(null)
    attach.reset()
    for (const f of files) {
      try {
        await attach.mutateAsync(await shrinkImage(f))
      } catch (e) {
        setShotError(e instanceof Error ? e.message : 'Myndin komst ekki inn.')
        return
      }
    }
  }
  const onPaste = (e: ClipboardEvent) => {
    const imgs = imagesFromClipboard(e.clipboardData)
    if (imgs.length > 0) {
      e.preventDefault()
      void addFiles(imgs)
    }
  }
  const copyBrief = async () => {
    setCopyError(null)
    try {
      const text = await api.work.brief(number)
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      setCopyError(e instanceof Error ? `Tókst ekki að afrita: ${e.message}` : 'Tókst ekki að afrita.')
    }
  }
  const sendDraft = () => {
    const text = draft.trim()
    if (!text || send.isPending) return
    send.mutate(text, { onSuccess: () => setDraft('') })
  }

  return (
    <div className="max-w-[1240px] mx-auto px-6 sm:px-10 py-8 pb-16 igf-fade">
      <Link to="/verkefni" search={{}} className="text-sm text-[var(--muted)] hover:text-[var(--brand)]">
        ← Verkefnalisti
      </Link>

      <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm text-[var(--faint)]">#{item.number}</span>
            <StatusBadge status={item.status} />
            {waitingOnMe && (
              <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ background: 'rgba(220,38,38,.12)', color: '#dc2626' }}>
                <span className="h-1.5 w-1.5 rounded-full bg-[#dc2626]" /> Bíður svars frá þér
              </span>
            )}
          </div>
          {editing ? (
            <TitleEditor
              value={item.title}
              onSave={(t) => {
                patch.mutate({ title: t })
                setEditing(false)
              }}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <h1 className="mt-1 font-grotesk text-[26px] font-bold leading-tight tracking-tight text-[var(--fg)]">
              {item.title}{' '}
              <button type="button" onClick={() => setEditing(true)} className="ml-2 align-middle text-sm font-medium text-[var(--brand)] hover:underline">
                Breyta heiti
              </button>
            </h1>
          )}
          <p className="mt-1 text-sm text-[var(--muted)]">
            Skráð af {item.createdByName} ({sideLabel(item.createdBySide)}) · {fmtWhen(item.createdAt)}
            {item.where && (
              <>
                {' '}
                · <span className="text-[var(--fg-soft)]">{item.where}</span>
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void copyBrief()}
          className="rounded-[11px] border border-[var(--border-strong)] px-3 py-2 text-sm font-medium text-[var(--fg-soft)] hover:border-[var(--brand)] hover:text-[var(--brand)]"
          title="Afritar beiðnina og samtalið sem texta — til að senda í tölvupósti eða rétta Claude"
        >
          {copied ? 'Afritað ✓' : 'Afrita sem texta'}
        </button>
        {copyError && <p className="w-full text-right text-xs text-[#dc2626]">{copyError}</p>}
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_260px]">
        <div className="space-y-5">
          <section className={`${card} p-5`}>
            <h2 className={`mb-2 ${h2}`}>Lýsing</h2>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-[var(--fg)]">{item.description}</p>
          </section>

          {side === 'team' && <ReviewPanel number={number} status={item.status} pending={item.review === 'pending'} review={data.review} onSettled={settle} />}

          <section className={`${card} p-5`} onPaste={onPaste}>
            <div className="mb-2 flex items-center justify-between">
              <h2 className={h2}>Skjámyndir {attachments.length > 0 && `(${attachments.length})`}</h2>
              <button type="button" onClick={() => fileRef.current?.click()} className="text-sm font-medium text-[var(--brand)] hover:underline">
                {attach.isPending ? 'Hleð upp…' : 'Bæta við mynd…'}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = [...(e.target.files ?? [])]
                  e.target.value = ''
                  void addFiles(files)
                }}
              />
            </div>
            {attachments.length === 0 ? (
              <p className="text-xs text-[var(--muted)]">Engin mynd enn. Límdu skjámynd hér (Ctrl+V) eða veldu skrá.</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {attachments.map((a) => (
                  <figure key={a.id} className="group relative">
                    <a href={a.dataUrl} target="_blank" rel="noreferrer">
                      <img src={a.dataUrl} alt="" className="w-full rounded-[11px] border border-[var(--border)] object-cover" />
                    </a>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm('Eyða mynd?\n\nMyndinni verður eytt af beiðninni.')) detach.mutate(a.id)
                      }}
                      className="absolute right-2 top-2 rounded-md bg-[var(--surface)]/90 px-2 py-0.5 text-xs text-[var(--fg-soft)] opacity-0 shadow transition group-focus-within:opacity-100 group-hover:opacity-100 hover:text-[#dc2626] focus-visible:opacity-100"
                    >
                      Eyða
                    </button>
                  </figure>
                ))}
              </div>
            )}
            {shotError && <p className="mt-2 text-xs text-[#dc2626]">{shotError}</p>}
            {detach.error instanceof Error && <p className="mt-2 text-xs text-[#dc2626]">Tókst ekki að eyða mynd: {detach.error.message}</p>}
          </section>

          <section className={`${card} p-5`}>
            <h2 className={`mb-3 ${h2}`}>Samtal</h2>
            {messages.length === 0 ? (
              <p className="mb-4 text-sm text-[var(--muted)]">
                {side === 'team'
                  ? 'Ekkert svar enn. Spurðu ef eitthvað er óljóst, eða segðu hvað verður gert.'
                  : 'Laufið hefur ekki svarað enn. Þú færð rauða tölu í valmyndinni þegar það gerist.'}
              </p>
            ) : (
              <ol className="mb-4 space-y-3">
                {messages.map((m) => {
                  const mine = m.side === side
                  return (
                    <li key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${mine ? 'rounded-br-sm' : 'rounded-bl-sm'}`}
                        style={mine ? { background: 'var(--brand)', color: 'var(--on-brand)' } : { background: 'var(--surface-2)', color: 'var(--fg)' }}
                      >
                        <div className="mb-0.5 text-[11px] opacity-80">
                          {sideLabel(m.side)} · {m.authorName} · {fmtWhen(m.createdAt)}
                        </div>
                        <div className="whitespace-pre-wrap leading-relaxed">{m.body}</div>
                      </div>
                    </li>
                  )
                })}
              </ol>
            )}
            <div className="flex flex-col gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={3}
                placeholder={side === 'team' ? `Svar eða spurning til ${CUSTOMER_NAME}…` : 'Svar eða viðbót…'}
                className="w-full rounded-[11px] border border-[var(--border-strong)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--fg)] outline-none transition focus:border-[var(--brand)]"
                onKeyDown={(e) => {
                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') sendDraft()
                }}
              />
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-[var(--faint)]">Ctrl+Enter sendir. Skilaboð færa boltann yfir til hinnar hliðarinnar.</span>
                <button
                  type="button"
                  disabled={!draft.trim() || send.isPending}
                  onClick={sendDraft}
                  className="rounded-[11px] bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-[var(--on-brand)] transition hover:bg-[var(--brand-strong)] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {send.isPending ? 'Sendi…' : 'Senda'}
                </button>
              </div>
              {send.error instanceof Error && <p className="text-xs text-[#dc2626]">{send.error.message}</p>}
            </div>
          </section>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          <section className={`${card} p-4`}>
            <h2 className={`mb-2 ${h2}`}>Staða</h2>
            <select value={item.status} onChange={(e) => patch.mutate({ status: e.target.value as WorkStatus })} className={selectCls}>
              {WORK_STATUSES.filter((s) => side === 'team' || s.key === item.status || !TEAM_ONLY_STATUSES.includes(s.key)).map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
            <p className="mt-1.5 text-xs text-[var(--muted)]">
              {side === 'team' ? 'Stöðubreyting telst svar: hún færir boltann til hinnar hliðarinnar.' : 'Laufið samþykkir, hafnar og setur í vinnslu. Þú getur lokað beiðninni sjálf/ur.'}
            </p>
          </section>

          <section className={`${card} p-4`}>
            <h2 className={`mb-2 ${h2}`}>Tegund</h2>
            <select value={item.kind} onChange={(e) => patch.mutate({ kind: e.target.value as WorkKind })} className={selectCls}>
              {WORK_KINDS.map((k) => (
                <option key={k.key} value={k.key}>
                  {k.icon} {k.label}
                </option>
              ))}
            </select>
            <p className="mt-1.5 text-xs text-[var(--muted)]">{kindMeta(item.kind).hint}</p>
          </section>

          <section className={`${card} p-4`}>
            <h2 className={`mb-2 ${h2}`}>Mikilvægi</h2>
            <div className="flex gap-1.5">
              {WORK_PRIORITIES.map((p) => {
                const on = item.priority === p.key
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => patch.mutate({ priority: p.key })}
                    aria-pressed={on}
                    className={`flex-1 rounded-[11px] border px-2 py-1.5 text-xs font-medium transition ${on ? 'border-[var(--brand)] bg-[var(--brand-soft)] text-[var(--brand)]' : 'border-[var(--border)] text-[var(--muted)] hover:border-[var(--border-strong)]'}`}
                  >
                    {p.label}
                  </button>
                )
              })}
            </div>
          </section>

          <section className={`${card} p-4`}>
            <h2 className={`mb-2 ${h2}`}>Hvar í kerfinu</h2>
            <PlaceInput
              value={where ?? item.where ?? ''}
              onChange={setWhere}
              onBlur={() => {
                if (where !== null && where.trim() !== (item.where ?? '')) patch.mutate({ where: where.trim() })
                setWhere(null)
              }}
            />
          </section>

          <section className={`${card} p-4 text-xs text-[var(--muted)]`}>
            <h2 className={`mb-2 ${h2}`}>Boltinn</h2>
            {statusMeta(item.status).open ? (
              <p>
                Bíður {sideGenitive(item.awaitingSide)} síðan {fmtWhen(item.activityAt)}.
              </p>
            ) : (
              <p>Beiðninni er lokið. Opnaðu hana aftur með því að breyta stöðunni.</p>
            )}
            {patch.error instanceof Error && <p className="mt-2 text-[#dc2626]">{patch.error.message}</p>}
          </section>
        </aside>
      </div>
    </div>
  )
}

function TitleEditor({ value, onSave, onCancel }: { value: string; onSave: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(value)
  return (
    <div className="mt-1 flex items-center gap-2">
      <input
        autoFocus
        value={v}
        onChange={(e) => setV(e.target.value)}
        maxLength={200}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && v.trim()) onSave(v.trim())
          if (e.key === 'Escape') onCancel()
        }}
        className="w-full rounded-[11px] border border-[var(--border-strong)] bg-[var(--surface)] px-3 py-1.5 text-xl font-bold text-[var(--fg)] outline-none focus:border-[var(--brand)]"
      />
      <button
        type="button"
        disabled={!v.trim()}
        onClick={() => onSave(v.trim())}
        className="rounded-[11px] bg-[var(--brand)] px-3 py-1.5 text-sm font-semibold text-[var(--on-brand)] disabled:opacity-40"
      >
        Vista
      </button>
      <button type="button" onClick={onCancel} className="text-sm text-[var(--muted)] hover:text-[var(--fg)]">
        Hætta við
      </button>
    </div>
  )
}
